"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useEffect, useRef } from "react";
import { Sparkles, X, Lock } from "lucide-react";
export type GateReason = "world" | "plot" | "limit" | "cta" | "wikiGenerate" | "wikiImage" | "wikiPublic";
const COPY: Record<GateReason, {
  title: string;
  body: string;
  dismissable: boolean;
}> = {
  world: {
    title: "Create an account to see how to build your world",
    body: "The visual Wiki — characters, locations, factions and the connections between them — is part of the full editor. Make a free account to start building your story's world.",
    dismissable: true
  },
  plot: {
    title: "Create an account to map your plot",
    body: "Plot threads and the timeline let you track every arc and event across your story. They're waiting for you in the full editor — it's free to start.",
    dismissable: true
  },
  limit: {
    title: "You've used all 40 free demo prompts",
    body: "Nice work! You've hit the demo's limit. Create a free account to keep writing with the AI assistant — and to actually save everything you make.",
    dismissable: true
  },
  cta: {
    title: "Keep what you create",
    body: "You're in a sandbox — nothing here is saved. Create a free account to keep this story, publish chapters, and earn from your readers. We take just 7%.",
    dismissable: true
  },
  wikiGenerate: {
    title: "Create an account to generate your Wiki",
    body: "Generate Wiki reads all your chapters and builds the entries for you. Create an account for free to try it.",
    dismissable: true
  },
  wikiImage: {
    title: "Create an account to generate images",
    body: "AI image generation is available with a free account. You can still upload a local image in this demo.",
    dismissable: true
  },
  wikiPublic: {
    title: "Create an account to publish your Wiki",
    body: "The demo can't create a public page. Create an account for free to publish Wiki pages for readers.",
    dismissable: true
  }
};
export function DemoGateModal(t0) {
  const $ = _c(32);
  const {
    reason,
    onClose
  } = t0;
  const dismissable = reason ? COPY[reason].dismissable : false;
  const panelRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== dismissable || $[1] !== onClose || $[2] !== reason) {
    t1 = () => {
      if (!reason || !dismissable) {
        return;
      }
      const onKey = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    };
    t2 = [reason, dismissable, onClose];
    $[0] = dismissable;
    $[1] = onClose;
    $[2] = reason;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== reason) {
    t3 = () => {
      if (reason) {
        panelRef.current?.focus();
      }
    };
    t4 = [reason];
    $[5] = reason;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  if (!reason) {
    return null;
  }
  const copy = COPY[reason];
  const t5 = copy.dismissable ? onClose : undefined;
  let t6;
  if ($[8] !== copy.dismissable || $[9] !== onClose) {
    t6 = copy.dismissable && <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"><X className="h-4 w-4" /></button>;
    $[8] = copy.dismissable;
    $[9] = onClose;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== reason) {
    t7 = <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10">{reason === "limit" ? <Sparkles className="h-5 w-5 text-brand" /> : <Lock className="h-5 w-5 text-brand" />}</div>;
    $[11] = reason;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== copy.title) {
    t8 = <h2 id="demo-gate-title" className="mb-2 text-lg font-semibold text-foreground">{copy.title}</h2>;
    $[13] = copy.title;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== copy.body) {
    t9 = <p className="mb-6 text-sm leading-relaxed text-muted-foreground">{copy.body}</p>;
    $[15] = copy.body;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Link href="/signup/writer" className="flex-1 rounded-full bg-brand px-4 py-2.5 text-center text-sm font-semibold text-white shadow-lg shadow-brand/30 transition hover:opacity-90">Create a free account</Link>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== copy.dismissable || $[19] !== onClose) {
    t11 = copy.dismissable && <button onClick={onClose} className="rounded-full px-4 py-2.5 text-sm font-medium text-muted-foreground transition hover:bg-muted">Keep exploring</button>;
    $[18] = copy.dismissable;
    $[19] = onClose;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11) {
    t12 = <div className="flex items-center gap-3">{t10}{t11}</div>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t13 = <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="demo-gate-title" tabIndex={-1} className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl outline-none" onClick={_temp}>{t6}{t7}{t8}{t9}{t12}</div>;
    $[23] = t12;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t5) {
    t14 = <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={t5}>{t13}</div>;
    $[29] = t13;
    $[30] = t5;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}
function _temp(e_0) {
  return e_0.stopPropagation();
}